/**
 * The ICU MessageFormat subset Crown catalogues may use:
 *
 *   {name}
 *   {n, plural, offset:1 =0 {none} one {# item} other {# items}}
 *   {x, select, a {…} other {…}}
 *   {d, date, short|medium|long}   {d, time, short|medium}
 *   {n, number}  {n, number, integer|percent|currency:GBP}
 *
 * Apostrophe quoting follows ICU: `'{'` is a literal brace, `''` is one
 * apostrophe, and an apostrophe not followed by a special character is text.
 */
export type DateStyle = 'short' | 'medium' | 'long';
export type TimeStyle = 'short' | 'medium';
export type NumberStyle = 'decimal' | 'integer' | 'percent' | { readonly currency: string };

export type MessageNode =
  | { readonly kind: 'text'; readonly value: string }
  | { readonly kind: 'arg'; readonly name: string }
  | { readonly kind: 'pound' }
  | {
      readonly kind: 'plural';
      readonly name: string;
      readonly offset: number;
      readonly options: Readonly<Record<string, readonly MessageNode[]>>;
    }
  | {
      readonly kind: 'select';
      readonly name: string;
      readonly options: Readonly<Record<string, readonly MessageNode[]>>;
    }
  | { readonly kind: 'date'; readonly name: string; readonly style: DateStyle }
  | { readonly kind: 'time'; readonly name: string; readonly style: TimeStyle }
  | { readonly kind: 'number'; readonly name: string; readonly style: NumberStyle };

export class MessageSyntaxError extends Error {
  readonly offset: number;
  constructor(message: string, offset: number) {
    super(`${message} (at offset ${offset})`);
    this.name = 'MessageSyntaxError';
    this.offset = offset;
  }
}

const ARG_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/;

function dateStyleOf(style: string): DateStyle | undefined {
  switch (style) {
    case '':
    case 'medium':
      return 'medium';
    case 'short':
    case 'long':
      return style;
    default:
      return undefined;
  }
}

function timeStyleOf(style: string): TimeStyle | undefined {
  switch (style) {
    case '':
    case 'short':
      return 'short';
    case 'medium':
      return 'medium';
    default:
      return undefined;
  }
}

class Parser {
  private pos = 0;
  constructor(private readonly src: string) {}

  parse(): MessageNode[] {
    const nodes = this.parseBody(false);
    if (this.pos < this.src.length) this.fail("unexpected '}'");
    return nodes;
  }

  /** Text and arguments until end of input, or until `}` when nested. */
  private parseBody(nested: boolean): MessageNode[] {
    const nodes: MessageNode[] = [];
    let text = '';
    const flush = () => {
      if (text) nodes.push({ kind: 'text', value: text });
      text = '';
    };
    while (this.pos < this.src.length) {
      const ch = this.src.charAt(this.pos);
      if (ch === '}') {
        if (!nested) this.fail("unexpected '}'");
        break;
      }
      if (ch === '{') {
        flush();
        nodes.push(this.parseArgument());
        continue;
      }
      if (ch === '#' && nested) {
        flush();
        nodes.push({ kind: 'pound' });
        this.pos += 1;
        continue;
      }
      if (ch === "'") {
        text += this.parseQuoted();
        continue;
      }
      text += ch;
      this.pos += 1;
    }
    flush();
    return nodes;
  }

  /** Reads from an apostrophe; returns the literal text it stands for. */
  private parseQuoted(): string {
    const next = this.src[this.pos + 1];
    if (next === "'") {
      this.pos += 2;
      return "'";
    }
    if (next === '{' || next === '}' || next === '#') {
      const end = this.src.indexOf("'", this.pos + 1);
      if (end === -1) {
        const rest = this.src.slice(this.pos + 1);
        this.pos = this.src.length;
        return rest;
      }
      const literal = this.src.slice(this.pos + 1, end).replaceAll("''", "'");
      this.pos = end + 1;
      return literal;
    }
    this.pos += 1;
    return "'";
  }

  private parseArgument(): MessageNode {
    const start = this.pos;
    this.pos += 1;
    const name = this.readUntil(',', '}').trim();
    if (!ARG_NAME.test(name)) this.fail('argument name expected', start);
    if (this.consume('}')) return { kind: 'arg', name };
    this.expect(',');
    const type = this.readUntil(',', '}').trim();
    switch (type) {
      case 'plural':
        return this.parsePlural(name);
      case 'select':
        return this.parseSelect(name);
      case 'date':
      case 'time':
      case 'number':
        return this.parseStyled(name, type);
      default:
        this.fail(`unknown argument type '${type}'`);
    }
  }

  private parseStyled(name: string, type: 'date' | 'time' | 'number'): MessageNode {
    let style = '';
    if (this.consume(',')) style = this.readUntil('}').trim();
    this.expect('}');
    if (type === 'date') {
      const chosen = dateStyleOf(style);
      if (!chosen) this.fail(`unknown date style '${style}'`);
      return { kind: 'date', name, style: chosen };
    }
    if (type === 'time') {
      const chosen = timeStyleOf(style);
      if (!chosen) this.fail(`unknown time style '${style}'`);
      return { kind: 'time', name, style: chosen };
    }
    if (style === '' || style === 'decimal') return { kind: 'number', name, style: 'decimal' };
    if (style === 'integer' || style === 'percent') return { kind: 'number', name, style };
    if (style.startsWith('currency:')) {
      const currency = style.slice('currency:'.length).trim();
      if (!/^[A-Z]{3}$/.test(currency)) this.fail(`currency code expected after 'currency:'`);
      return { kind: 'number', name, style: { currency } };
    }
    this.fail(`unknown number style '${style}'`);
  }

  private parsePlural(name: string): MessageNode {
    this.expect(',');
    let offset = 0;
    this.skipSpace();
    if (this.src.startsWith('offset:', this.pos)) {
      this.pos += 'offset:'.length;
      const digits = this.readWhile((c) => c >= '0' && c <= '9');
      if (!digits) this.fail('offset needs a number');
      offset = Number(digits);
    }
    const options = this.parseOptions((key) => /^(=\d+|zero|one|two|few|many|other)$/.test(key));
    this.expect('}');
    if (!options.other) this.fail(`plural '${name}' needs an 'other' option`);
    return { kind: 'plural', name, offset, options };
  }

  private parseSelect(name: string): MessageNode {
    this.expect(',');
    const options = this.parseOptions((key) => /^[A-Za-z0-9_]+$/.test(key));
    this.expect('}');
    if (!options.other) this.fail(`select '${name}' needs an 'other' option`);
    return { kind: 'select', name, options };
  }

  private parseOptions(isValidKey: (key: string) => boolean) {
    const options: Record<string, MessageNode[]> = {};
    for (;;) {
      this.skipSpace();
      if (this.pos >= this.src.length || this.src[this.pos] === '}') break;
      const keyStart = this.pos;
      const key = this.readWhile((c) => c !== '{' && c !== '}' && !/\s/.test(c));
      if (!key || !isValidKey(key)) this.fail(`option key expected`, keyStart);
      this.skipSpace();
      this.expect('{');
      options[key] = this.parseBody(true);
      this.expect('}');
    }
    return options;
  }

  private readUntil(...stops: string[]): string {
    const start = this.pos;
    while (this.pos < this.src.length && !stops.includes(this.src.charAt(this.pos))) this.pos += 1;
    if (this.pos >= this.src.length) this.fail("unclosed '{'", start - 1);
    return this.src.slice(start, this.pos);
  }

  private readWhile(test: (c: string) => boolean): string {
    const start = this.pos;
    while (this.pos < this.src.length && test(this.src.charAt(this.pos))) this.pos += 1;
    return this.src.slice(start, this.pos);
  }

  private skipSpace(): void {
    this.readWhile((c) => /\s/.test(c));
  }

  private consume(ch: string): boolean {
    this.skipSpace();
    if (this.src[this.pos] === ch) {
      this.pos += 1;
      return true;
    }
    return false;
  }

  private expect(ch: string): void {
    if (!this.consume(ch)) {
      this.fail(this.pos >= this.src.length ? "unclosed '{'" : `'${ch}' expected`);
    }
  }

  private fail(message: string, offset = this.pos): never {
    throw new MessageSyntaxError(message, offset);
  }
}

export function parseMessage(source: string): MessageNode[] {
  return new Parser(source).parse();
}

/** Argument names a message reads, including those inside plural and select branches. */
export function argumentNames(nodes: readonly MessageNode[]): Set<string> {
  const names = new Set<string>();
  const walk = (list: readonly MessageNode[]) => {
    for (const node of list) {
      if (node.kind === 'text' || node.kind === 'pound') continue;
      names.add(node.name);
      if (node.kind === 'plural' || node.kind === 'select') {
        for (const branch of Object.values(node.options)) walk(branch);
      }
    }
  };
  walk(nodes);
  return names;
}

/** `argument:option` pairs for every plural and select, so translations can be checked for parity. */
export function branchKeys(nodes: readonly MessageNode[]): Set<string> {
  const keys = new Set<string>();
  const walk = (list: readonly MessageNode[]) => {
    for (const node of list) {
      if (node.kind !== 'plural' && node.kind !== 'select') continue;
      for (const [option, branch] of Object.entries(node.options)) {
        keys.add(`${node.name}:${option}`);
        walk(branch);
      }
    }
  };
  walk(nodes);
  return keys;
}
